import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Copy, RefreshCw, Loader2, Mail, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_ROOT = process.env.REACT_APP_BACKEND_URL;

export const CalendarFeedCard = () => {
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regen, setRegen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/calendar-feed`);
      setFeed(data);
    } catch {
      toast.error("Could not load calendar feed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fullUrl = feed ? `${PUBLIC_ROOT}${feed.path}` : "";
  const gcalUrl = feed ? `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(fullUrl)}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("URL copied to clipboard.");
    } catch {
      toast.error("Copy failed — long-press to select.");
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate the feed URL? The old URL will stop working immediately and you'll need to re-subscribe on each device.")) return;
    setRegen(true);
    try {
      const { data } = await axios.post(`${API}/admin/calendar-feed/regenerate`);
      setFeed(data);
      toast.success("New URL generated.");
    } catch {
      toast.error("Could not regenerate.");
    } finally {
      setRegen(false);
    }
  };

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 md:p-7" data-testid="admin-calendar-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#B76E79] to-[#8C4E58] text-white flex items-center justify-center flex-shrink-0">
          <Calendar size={16} />
        </div>
        <div>
          <h3 className="font-serif text-xl text-[#14100F]">Google Calendar sync</h3>
          <p className="text-sm text-[#6A5F58] mt-1">Subscribe once and every booking auto-appears in your Google Calendar (also iPhone/Apple/Outlook) as an all-day arrival + departure event with guest details.</p>
        </div>
      </div>

      {loading && <div className="mt-4 text-center py-4 text-[#6A5F58]"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>}
      {feed && (
        <>
          <div className="mt-5">
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Your private feed URL</label>
            <div className="mt-1 flex items-center gap-2">
              <input
                readOnly
                value={fullUrl}
                className="flex-1 h-10 rounded-lg border border-[#DDD1BE] bg-[#F5EFE6] px-3 text-xs font-mono text-[#14100F]"
                data-testid="calendar-feed-url"
              />
              <button onClick={copy} className="inline-flex items-center gap-1 h-10 px-3 rounded-lg border border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white text-xs uppercase tracking-[0.14em]" data-testid="calendar-feed-copy">
                <Copy size={12} /> Copy
              </button>
              <button onClick={regenerate} disabled={regen} className="inline-flex items-center gap-1 h-10 px-3 rounded-lg border border-[#DDD1BE] text-[#0F0E0E] hover:bg-[#F5EFE6] text-xs uppercase tracking-[0.14em] disabled:opacity-50" data-testid="calendar-feed-regen">
                {regen ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Regen
              </button>
            </div>
          </div>

          <div className="mt-5 p-4 rounded-xl bg-[#F5EFE6] border border-[#DDD1BE] text-sm text-[#14100F]">
            <p className="font-semibold mb-2">📱 How to subscribe (one-time · 30 seconds)</p>
            <ol className="list-decimal pl-5 space-y-1 text-[13px]">
              <li>Open <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="text-[#B76E79] hover:underline">Google Calendar</a> on desktop.</li>
              <li>Click the <strong>+</strong> next to "Other calendars" → <strong>From URL</strong>.</li>
              <li>Paste the URL above → <strong>Add calendar</strong>.</li>
              <li>Done. It syncs to your phone/laptop/Apple Watch automatically.</li>
            </ol>
            <a href={gcalUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] text-[#B76E79] hover:text-[#8C4E58]" data-testid="calendar-feed-gcal">
              Or click here to open in Google Calendar →
            </a>
          </div>
          <p className="mt-3 text-[11px] text-[#6A5F58]">
            Keep this URL private — anyone with it can see your bookings. Click "Regen" to invalidate it if it leaks.
          </p>
        </>
      )}
    </div>
  );
};

export const DailyBriefCard = () => {
  const [busy, setBusy] = useState(false);
  const trigger = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/daily-brief/test`);
      if (data.sent) toast.success(`Sent to ${data.to} — ${data.arrivals} arrivals, ${data.departures} departures.`);
      else toast.error("Not sent — check ADMIN_NOTIFY_EMAIL setting.");
    } catch {
      toast.error("Trigger failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 md:p-7" data-testid="admin-daily-brief-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] flex items-center justify-center flex-shrink-0">
          <Mail size={16} />
        </div>
        <div className="flex-1">
          <h3 className="font-serif text-xl text-[#14100F]">Morning arrivals email</h3>
          <p className="text-sm text-[#6A5F58] mt-1">
            Every day at <strong>07:00 GMT</strong> you'll get a brief listing today's arrivals and departures with guest name, phone, pitch, vehicle registration, and any special requests or admin notes.
          </p>
          <button onClick={trigger} disabled={busy} className="mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-gradient-to-br from-[#B76E79] to-[#8C4E58] text-white text-xs uppercase tracking-[0.14em] hover:from-[#C48690] hover:to-[#A75E68] disabled:opacity-50" data-testid="daily-brief-test">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send test email now
          </button>
        </div>
      </div>
    </div>
  );
};
